import { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableState,
    useReactTable
} from "@tanstack/react-table";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedAxnAdminSession } from "@idw-types/data/management-console/axnadmin";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./axn-admin-sessions-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type AxnAdminSessionsTableContainerProps = {
    sessions: Array<FormattedAxnAdminSession>;
    tableState: TableState;
    isPageHeight: boolean;
};

type AxnAdminSessionsTableDataRow = FormattedAxnAdminSession & {
    organization: string;
};

export const AxnAdminSessionsTableContainer = (props: AxnAdminSessionsTableContainerProps) => {
    const { sessions, tableState, isPageHeight } = props;
    const dispatch = useDispatch();

    const sessionTableData: Array<AxnAdminSessionsTableDataRow> = useMemo(() => {
        return sessions.map(session => ({
            ...session,
            organization: session.organization || resources.common.notFound
        }));
    }, [sessions]);

    const columnHelper = createColumnHelper<AxnAdminSessionsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("userID", {
            header: resources.tables.axnAdminSessions.columns.userId.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("role", {
            header: resources.tables.axnAdminSessions.columns.role.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("organization", {
            header: resources.tables.axnAdminSessions.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organization, b.original.organization)
        }),
        columnHelper.accessor("firstName", {
            header: resources.tables.axnAdminSessions.columns.firstName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.firstName, b.original.firstName)
        }),
        columnHelper.accessor("lastName", {
            header: resources.tables.axnAdminSessions.columns.lastName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.lastName, b.original.lastName)
        }),
        columnHelper.accessor("email", {
            header: resources.tables.axnAdminSessions.columns.email.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.email, b.original.email)
        }),
        columnHelper.accessor("logInDate", {
            header: resources.tables.axnAdminSessions.columns.logInDate.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.logInDate, b.original.logInDate)
        }),
        columnHelper.accessor("lastAccessDate", {
            header: resources.tables.axnAdminSessions.columns.lastAccessDate.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.lastAccessDate, b.original.lastAccessDate)
        })
    ]), [sessionTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: sessionTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.AXN_ADMIN_SESSIONS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.AXN_ADMIN_SESSIONS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.AXN_ADMIN_SESSIONS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.axnAdminSessionsTableContainer}>
            <IdwTable
                id="axn-admin-sessions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"axn-admin-sessions"}
                className={styles.axnAdminSessionsTable}
            />
            <PaginationContainer
                idPrefix="axn-admin-sessions"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};